import React, { FC, useState } from 'react';
import { IReviewNew, IReviewNewProps } from '../../../shared/interfaces/IReviewNew';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import ReviewWrapper from '../ReviewWrapper';
import ReviewHeader from '../ReviewHeader';
import ReviewContent from '../ReviewContent';
import ReviewFooter from '../ReviewFooter';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import ReviewText from '../ReviewText';
import { ExpandLess, MoreVert, Person } from '@mui/icons-material';
import Block from '../../Block/Block';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import Status from '../../Status/Status';
import OpinionIndicator from '../../OpinionIndicator';
import CloseIcon from '@mui/icons-material/Close';

interface Props extends IReviewNewProps {}

const JobvotingReview: FC<Props> = ({
  communityManagement,
  commentingModule,
  hideHeader,
  hideFooter,
  statusModule,
  status,
  deletionModule,
  reviewEntry,
}) => {
  const theme = useTheme();
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const { t } = useTranslation();

  const { name, rating, text, reviewTitle, companyComment, header, blocks } = reviewEntry.review;

  return (
    <ReviewWrapper
      communityManagement={communityManagement}
      commentingModule={commentingModule}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      rating={rating}
      statusModule={statusModule}
      status={status}
      deletionModule={deletionModule}
      reviewEntry={reviewEntry}
    >
      {name && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.text.secondary,
          }}
        >
          <Person fontSize="small" />
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {name}
          </Typography>
        </Box>
      )}
      {header && (
        <Typography
          variant="h6"
          sx={{ mt: theme.spacing(2) }}
        >
          {header}
        </Typography>
      )}
      {reviewTitle && (
        <Typography
          variant="h6"
          sx={{ mt: theme.spacing(2) }}
        >
          {reviewTitle}
        </Typography>
      )}
      <ReviewText text={text} />
      {blocks !== undefined && blocks.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            mt: theme.spacing(3),
            ml: theme.spacing(7),
            mb: theme.spacing(3),
          }}
        >
          {blocks
            .filter((value) => typeof value.rating === 'string')
            .filter((value) => value.title !== null)
            .map(({ rating, title }, index) => {
              return (
                <Box
                  key={`Review_${reviewEntry.id}_Block_${index}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                  }}
                >
                  {rating === 'YES' ? (
                    <>
                      <CheckIcon sx={{ color: theme.palette.success.dark }} />
                      <Status
                        key={`Review_${reviewEntry.id}_Block_${index}`}
                        text={title}
                        iconBefore={<OpinionIndicator rating={rating} />}
                        sx={{ mx: theme.spacing(1) }}
                      />
                    </>
                  ) : (
                    <>
                      <CloseIcon sx={{ color: theme.palette.error.dark }} />
                      <Status
                        key={`Review_${reviewEntry.id}_Block_${index}`}
                        text={title}
                        iconBefore={<OpinionIndicator rating={rating} />}
                        sx={{ mx: theme.spacing(1) }}
                      />
                    </>
                  )}
                </Box>
              );
            })}
        </Box>
      )}
      {blocks && blocks.length > 0 && !showAllBlocks && (
        <Box sx={{ mt: theme.spacing(1) }}>
          <Divider orientation="horizontal" />
          <Block
            rating={blocks.filter((value) => typeof value.rating === 'number')[0].rating}
            text={blocks.filter((value) => typeof value.rating === 'number')[0].text}
            title={blocks.filter((value) => typeof value.rating === 'number')[0].title}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<MoreVert />}
            onClick={() => setShowAllBlocks(!showAllBlocks)}
            sx={{
              my: theme.spacing(1),
            }}
          >
            {t('show_more')}
          </Button>
        </Box>
      )}
      {blocks && blocks.length > 0 && showAllBlocks && (
        <Box sx={{ mt: theme.spacing(3) }}>
          {blocks
            .filter((value) => typeof value.rating === 'number')
            .map(({ rating, text, title }, index) => {
              return (
                <Box
                  key={`Review_${reviewEntry.id}_Block_${index}`}
                  sx={{ mt: theme.spacing(1) }}
                >
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    text={text}
                    title={title}
                  />
                </Box>
              );
            })}
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExpandLess />}
            onClick={() => setShowAllBlocks(!showAllBlocks)}
            sx={{
              mb: theme.spacing(1),
            }}
          >
            {t('show_less')}
          </Button>
        </Box>
      )}
    </ReviewWrapper>
  );
};

export default JobvotingReview;
